import { Box, Card, CardContent, Divider, Typography } from '@mui/material';
import { Link, ReferenceManyField, TextField, useRecordContext, useTranslate } from 'react-admin';

import {
  B2bCompanyDocumentDetails,
  GetB2bCompaniesIdEndpoint,
} from '@Common/types/bo/b2b/companies';

import { DatagridTyped } from '@sh4res/bo-common/components';
import { mockLatestDocuments } from '@sh4res/bo-common/components/B2bCompanyDashboard/mock_data';
import { B2bCompanyDashboardProps } from '@sh4res/bo-common/components/B2bCompanyDashboard/utils';
import { UTCDateField } from '@sh4res/bo-common/fields';
import { DocumentListViewerLink } from '@sh4res/bo-common/fields/DocumentListViewerLink';
import { ColumnList } from '@sh4res/bo-common/utils';
import { buildLink } from '@sh4res/bo-common/utils/build_link';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

const testId = 'BoExternalB2bCompanyDashboardDocumentsSection';

const getDashboardDocumentColumns = () =>
  ({
    // eslint-disable-next-line @sh4res/jsx-id-field-for-resources -- no show page
    id: <TextField data-testid={`${testId}_IdField`} />,
    document_name: <TextField data-testid={`${testId}_DocumentNameField`} />,
    document_url: (
      <DocumentListViewerLink<B2bCompanyDocumentDetails>
        urlSource="document_url"
        nameSource="document_name"
        label="resources.bo_common.b2b.documents.fields.document_name"
      />
    ),
    created_at: <UTCDateField data-testid={`${testId}_CreatedAtField`} />,
  }) as ColumnList<B2bCompanyDocumentDetails>;

export const DocumentsSection = ({ useMock, resource }: B2bCompanyDashboardProps) => {
  const translate = useTranslate();
  const record = useRecordContext<GetB2bCompaniesIdEndpoint['Client']['ResponseBody']>();

  // For mock mode, check if we have data
  if (useMock && mockLatestDocuments.length === 0) {
    return null;
  }

  const displayedColumns = isBoExternal()
    ? (['document_url', 'created_at'] as const)
    : (['id', 'document_name', 'document_url', 'created_at'] as const);

  const tableStyles = {
    '& .RaDatagrid-headerCell, & .RaDatagrid-rowCell': {
      padding: '12px 20px',
    },
  };

  return (
    <Card sx={{ margin: '2px 2px 15px 2px', borderRadius: '8px' }}>
      <CardContent sx={{ padding: 0, paddingBottom: '0px !important' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 20px 15px 20px',
          }}>
          <Typography variant="h3" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            {translate(`resources.${resource}.dashboard.titles.latest_documents`)}
          </Typography>
          <Link
            to={
              isBoExternal()
                ? '/documents?tab=company'
                : `/${resource}/${record?.id}/show?tab=documents_company`
            }
            style={{ textDecoration: 'none' }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontWeight: 500,
                color: '#1976d2',
                textDecoration: 'underline',
                '&:hover': {
                  textDecoration: 'none',
                },
              }}>
              {translate(`resources.${resource}.dashboard.see_all`)}
            </Typography>
          </Link>
        </Box>

        <Divider />

        {useMock ? (
          <DatagridTyped<B2bCompanyDocumentDetails>
            data={mockLatestDocuments.slice(0, 3)}
            columnRegistry={getDashboardDocumentColumns()}
            displayedColumns={displayedColumns}
            bulkActionButtons={false}
            showCard={false}
            sortableColumns={[]}
            sort={{ field: 'created_at', order: 'DESC' }}
            rowClick={(id) => buildLink(`${resource}/documents`, 'show', id)}
            sx={tableStyles}
          />
        ) : (
          <ReferenceManyField
            data-testid={`${testId}_DocumentsListField`}
            target="company_id"
            reference={`${resource}/documents`}
            label={false}
            filter={{ is_archived: false }}
            sort={{ field: 'created_at', order: 'DESC' }}
            perPage={3}
            pagination={<></>}>
            <DatagridTyped<B2bCompanyDocumentDetails>
              columnRegistry={getDashboardDocumentColumns()}
              displayedColumns={displayedColumns}
              bulkActionButtons={false}
              showCard={false}
              sortableColumns={[]}
              rowClick={(id) => buildLink(`${resource}/documents`, 'show', id)}
              sx={tableStyles}
            />
          </ReferenceManyField>
        )}
      </CardContent>
    </Card>
  );
};
